import { GeneralAssignmentConsolidatedService } from './generalAssignmentConsolidatedService';
import { ItemCardService } from './itemCardService';
import { StudentItemCard, ItemCard } from '../types/itemCard';
import { StudentCoinService } from './studentCoinService';

/**
 * 일반 과제 보상 관리 서비스
 */
export class GeneralAssignmentRewardService {
  private userId: string;
  private classId: string;
  private generalAssignmentService: GeneralAssignmentConsolidatedService;
  private itemCardService: ItemCardService;
  private coinService: StudentCoinService;

  constructor(userId: string, classId: string) {
    this.userId = userId;
    this.classId = classId;
    this.generalAssignmentService = new GeneralAssignmentConsolidatedService(userId, classId);
    this.itemCardService = new ItemCardService(userId, classId);
    this.coinService = new StudentCoinService(userId, classId);
  }

  /**
   * 일반 과제 보상 수동 지급 처리
   * 선택된 멤버들에게 보상을 지급
   */
  async grantRewardsToStudents(
    assignmentId: string,
    studentIds: string[]
  ): Promise<{
    success: boolean;
    grantedCount: number;
    message: string;
  }> {
    try {
      console.log('🎁 일반 과제 보상 지급 시작:', { assignmentId, studentIds });

      // 일반 과제 정보 로드
      const generalAssignment = await this.generalAssignmentService.loadGeneralAssignment(assignmentId);
      if (!generalAssignment) {
        console.log('❌ 일반 과제를 찾을 수 없습니다:', assignmentId);
        return { success: false, grantedCount: 0, message: '일반 과제를 찾을 수 없습니다.' };
      }

      console.log('🔍 로드된 일반 과제 정보:', {
        assignmentId: generalAssignment.assignmentId,
        title: generalAssignment.title,
        rewardEnabled: generalAssignment.rewardEnabled,
        rewardCoins: generalAssignment.rewardCoins,
        rewardItemCardIds: generalAssignment.rewardItemCardIds
      });

      // 보상 활성화 여부 확인
      if (!generalAssignment.rewardEnabled) {
        console.log('📝 일반 과제 보상이 비활성화되어 있습니다:', assignmentId);
        return { success: false, grantedCount: 0, message: '일반 과제 보상이 비활성화되어 있습니다.' };
      }

      // 보상이 설정되지 않은 경우 처리하지 않음
      const hasCoins = generalAssignment.rewardCoins && generalAssignment.rewardCoins > 0;
      const hasItemCards = generalAssignment.rewardItemCardIds && generalAssignment.rewardItemCardIds.length > 0;

      console.log('🔍 보상 설정 확인:', { hasCoins, hasItemCards, rewardItemCardIds: generalAssignment.rewardItemCardIds });

      if (!hasCoins && !hasItemCards) {
        console.log('📝 설정된 보상이 없습니다:', assignmentId);
        return { success: false, grantedCount: 0, message: '설정된 보상이 없습니다.' };
      }

      console.log('🎯 일반 과제 보상 정보:', {
        rewardCoins: generalAssignment.rewardCoins,
        rewardItemCardIds: generalAssignment.rewardItemCardIds
      });

      // 아이템 카드 정보 로드 (있는 경우)
      let rewardItemCards: ItemCard[] = [];
      if (hasItemCards) {
        const itemCardSet = await this.itemCardService.loadItemCardSet();
        console.log('🎯 itemCardSet 로드 결과:', {
          itemCardSetExists: !!itemCardSet,
          assignmentItemCount: itemCardSet?.assignmentItem?.length || 0,
          assignmentItemIds: itemCardSet?.assignmentItem?.map(item => item.itemCardId) || []
        });

        if (itemCardSet) {
          console.log('🎯 generalAssignment.rewardItemCardIds:', generalAssignment.rewardItemCardIds);

          rewardItemCards = (generalAssignment.rewardItemCardIds || [])
            .map(itemCardId => {
              const foundItem = itemCardSet.assignmentItem.find(item => item.itemCardId === itemCardId);
              console.log(`🎯 아이템 카드 검색 - ${itemCardId}:`, foundItem ? '찾음' : '찾을 수 없음', foundItem?.name);
              return foundItem;
            })
            .filter((item): item is ItemCard => item !== undefined);

          console.log('🎯 최종 보상 아이템 카드:', rewardItemCards.map(item => ({ id: item.itemCardId, name: item.name })));
        } else {
          console.log('❌ itemCardSet을 로드할 수 없습니다.');
        }
      }

      // 기존 보상 지급 완료 목록
      const currentGrantedStudentIds = generalAssignment.rewardGrantedStudentIds || [];
      const newGrantedStudentIds: string[] = [...currentGrantedStudentIds];
      let grantedCount = 0;

      // 각 멤버에 대해 보상 지급 처리
      for (const studentId of studentIds) {
        const alreadyGranted = currentGrantedStudentIds.includes(studentId);
        
        if (!alreadyGranted) {
          // 보상을 받지 않은 경우 -> 보상 지급
          console.log(`✅ 멤버 ${studentId} 보상 지급`);
          await this.grantRewardsToStudent(
            studentId, 
            rewardItemCards, 
            assignmentId,
            generalAssignment.rewardCoins || 0,
            generalAssignment.title
          );
          newGrantedStudentIds.push(studentId);
          grantedCount++;
        } else {
          // 이미 보상을 받은 경우 -> 목록에 유지
          console.log(`📝 멤버 ${studentId}는 이미 보상을 받았습니다`);
        }
      }

      // 보상 지급 완료 목록 업데이트
      await this.generalAssignmentService.updateGeneralAssignment(
        assignmentId,
        generalAssignment.title,
        generalAssignment.description,
        new Date(generalAssignment.dueDate),
        generalAssignment.basePoints,
        generalAssignment.assignedStudentIds,
        generalAssignment.categoryId,
        generalAssignment.isActive,
        generalAssignment.icon,
        generalAssignment.notes,
        generalAssignment.rewardEnabled,
        generalAssignment.rewardCode,
        generalAssignment.rewardMessage,
        generalAssignment.rewardCoins,
        generalAssignment.rewardItemCardIds,
        newGrantedStudentIds // rewardGrantedStudentIds
      );

      console.log('✅ 일반 과제 보상 지급 완료:', { grantedCount });
      return {
        success: true,
        grantedCount,
        message: `보상지급 ${grantedCount}명\n총 보상지급자 수: ${newGrantedStudentIds.length}명`
      };
    } catch (error) {
      console.error('❌ 일반 과제 보상 지급 실패:', error);
      return {
        success: false,
        grantedCount: 0,
        message: '보상 지급 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * 일반 과제 보상 수동 회수 처리
   * 선택된 멤버들의 보상을 회수
   */
  async revokeRewardsFromStudents(
    assignmentId: string,
    studentIds: string[]
  ): Promise<{
    success: boolean;
    revokedCount: number;
    message: string;
  }> {
    try {
      console.log('🎁 일반 과제 보상 회수 시작:', { assignmentId, studentIds });

      // 일반 과제 정보 로드
      const generalAssignment = await this.generalAssignmentService.loadGeneralAssignment(assignmentId);
      if (!generalAssignment) {
        console.log('❌ 일반 과제를 찾을 수 없습니다:', assignmentId);
        return { success: false, revokedCount: 0, message: '일반 과제를 찾을 수 없습니다.' };
      }

      // 기존 보상 지급 완료 목록
      const currentGrantedStudentIds = generalAssignment.rewardGrantedStudentIds || [];
      const newGrantedStudentIds: string[] = [];
      let revokedCount = 0;

      // 각 멤버에 대해 보상 회수 처리
      for (const studentId of studentIds) {
        const hasReward = currentGrantedStudentIds.includes(studentId);
        
        if (hasReward) {
          // 보상을 받은 경우 -> 보상 회수
          console.log(`⏳ 멤버 ${studentId} 보상 회수`);
          await this.revokeRewardsFromStudent(
            studentId, 
            assignmentId,
            generalAssignment.rewardCoins || 0,
            generalAssignment.title
          );
          revokedCount++;
        } else {
          // 보상을 받지 않은 경우 -> 목록에 추가하지 않음
          console.log(`📝 멤버 ${studentId}는 보상을 받지 않았습니다`);
        }
      }

      // 보상 지급 완료 목록 업데이트 (제거된 멤버은 목록에서 제외)
      currentGrantedStudentIds.forEach(studentId => {
        if (!studentIds.includes(studentId)) {
          newGrantedStudentIds.push(studentId);
        }
      });

      await this.generalAssignmentService.updateGeneralAssignment(
        assignmentId,
        generalAssignment.title,
        generalAssignment.description,
        new Date(generalAssignment.dueDate),
        generalAssignment.basePoints,
        generalAssignment.assignedStudentIds,
        generalAssignment.categoryId,
        generalAssignment.isActive,
        generalAssignment.icon,
        generalAssignment.notes,
        generalAssignment.rewardEnabled,
        generalAssignment.rewardCode,
        generalAssignment.rewardMessage,
        generalAssignment.rewardCoins,
        generalAssignment.rewardItemCardIds,
        newGrantedStudentIds // rewardGrantedStudentIds - 빈 배열도 전달하여 서버에서 삭제되도록
      );

      console.log('✅ 일반 과제 보상 회수 완료:', { revokedCount });
      return {
        success: true,
        revokedCount,
        message: `보상회수 ${revokedCount}명\n총 보상지급자 수: ${newGrantedStudentIds.length}명`
      };
    } catch (error) {
      console.error('❌ 일반 과제 보상 회수 실패:', error);
      return {
        success: false,
        revokedCount: 0,
        message: '보상 회수 중 오류가 발생했습니다.'
      };
    }
  }

  /**
   * 멤버에게 보상 지급 (코인 + 아이템)
   */
  private async grantRewardsToStudent(
    studentId: string,
    rewardItemCards: ItemCard[],
    assignmentId: string,
    rewardCoins: number = 0,
    assignmentTitle: string = '일반 과제'
  ): Promise<void> {
    try {
      // 1. 코인 지급
      if (rewardCoins > 0) {
        await this.coinService.grantCoinsToStudent(
          studentId,
          rewardCoins,
          `${assignmentTitle} 과제 완료 보상`,
          'General'
        );
        console.log(`💰 코인 지급: ${rewardCoins}코인 (멤버: ${studentId}, 과제: ${assignmentId})`);
      }

      // 2. 아이템 카드 지급 (잠금/해제 방식으로 통일)
      if (rewardItemCards.length > 0) {
        const studentItemCards = await this.itemCardService.getStudentItemCards(studentId);
        const existingRewards = studentItemCards.filter(card => card.assignmentId === assignmentId);

        if (existingRewards.length > 0) {
          await this.itemCardService.unlockStudentItemCardsByAssignment(studentId, assignmentId);
        }

        const newRewards = rewardItemCards.filter(rewardItem =>
          !existingRewards.some(existing => existing.itemCardId === rewardItem.itemCardId)
        );

        for (const rewardItem of newRewards) {
          const studentItemCard: StudentItemCard = {
            instanceId: `assignment_${assignmentId}_${rewardItem.itemCardId}_${Date.now()}`,
            itemCardId: rewardItem.itemCardId,
            type: rewardItem.type,
            ...(rewardItem.useCount !== undefined && { remainingUseCount: rewardItem.useCount }),
            acquiredAt: new Date(),
            assignmentId: assignmentId,
            category: 'assignment',
            isLocked: false
          };

          await this.itemCardService.addStudentItemCardAtomic(studentId, studentItemCard);
          console.log(`🎁 아이템 보상 지급: ${rewardItem.name} (멤버: ${studentId})`);
        }
      }
    } catch (error) {
      console.error(`❌ 멤버 ${studentId} 보상 지급 실패:`, error);
      throw error;
    }
  }

  /**
   * 멤버으로부터 보상 회수 (코인 + 아이템)
   */
  private async revokeRewardsFromStudent(
    studentId: string,
    assignmentId: string,
    rewardCoins: number = 0,
    assignmentTitle: string = '일반 과제'
  ): Promise<void> {
    try {
      // 1. 코인 회수 (음수로 지급하여 총액 조정)
      if (rewardCoins > 0) {
        await this.coinService.grantCoinsToStudent(
          studentId,
          -rewardCoins,
          `${assignmentTitle} 과제 보상 회수`,
          'General'
        );
        console.log(`💰 코인 회수: -${rewardCoins}코인 (멤버: ${studentId}, 과제: ${assignmentId})`);
      }

      // 2. 아이템 카드 잠금 (삭제하지 않음)
      await this.itemCardService.lockStudentItemCardsByAssignment(studentId, assignmentId);
      console.log(`🔒 아이템 보상 회수 완료(잠금): 멤버 ${studentId}, 과제 ${assignmentId}`);
    } catch (error) {
      console.error(`❌ 멤버 ${studentId} 보상 회수 실패:`, error);
      throw error;
    }
  }
}

